/**
 * Unified Practitioner Session Generation Hook
 *
 * Replaces the complex chain of useAudioGeneration + useEnhancedSessionGeneration
 * with a single, simplified hook focused on practitioner sessions.
 */

import { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { createPractitionerSessionAPI, PractitionerSessionCreateRequest } from '../utils/practitionerSessionApi';
import { mapQuestionnaireToBackendSchema, QuestionnaireData } from '../utils/questionnaireMapper';

// ============================================================================
// TYPES
// ============================================================================

export interface PractitionerSessionConfig {
  // Required
  title: string;
  clientId?: string;
  groupSessionId?: string;
  categoryId: string;

  // Questionnaire data
  questionnaireData: QuestionnaireData;

  // Optional overrides
  sessionNotes?: string;
  customPrompt?: string;
  contentExclusions?: string[];
  contentRequirements?: string[];
  sessionDirectives?: string;

  // Custom text mode fields
  custom_text_mode?: 'verbatim' | 'blend_beginning' | 'blend_end' | 'blend_middle' | 'blend_woven' | 'blend_custom';
  custom_text?: string;
  custom_text_instructions?: string;

  // Client info (if not in questionnaire)
  clientFirstName?: string;
  clientLastName?: string;
  practitionerName?: string;

  // Audio options
  voiceGender?: string;
  specific_voice_id?: string;
  backgroundMusicFile?: string;
  saveMixedVersion?: boolean;
  backgroundMusicVolumeDb?: number;
}

export interface SessionGenerationResult {
  sessionId: string;
  audioUrl: string;
  mixedAudioUrl?: string;
  backgroundAudioUrl?: string; // Full URL to background music (single source of truth)
  generatedContent: string;
}

export interface SessionGenerationState {
  isGenerating: boolean;
  progress: number;
  error: string | null;
  result: SessionGenerationResult | null;
}

// ============================================================================
// HOOK
// ============================================================================

export function usePractitionerSessionGeneration() {
  const { session } = useAuth();

  const [state, setState] = useState<SessionGenerationState>({
    isGenerating: false,
    progress: 0,
    error: null,
    result: null,
  });

  // Create API client with token provider
  const apiClient = useMemo(() => {
    return createPractitionerSessionAPI(async () => {
      return session?.access_token || null;
    });
  }, [session?.access_token]);

  /**
   * Generate a practitioner session
   */
  const generateSession = useCallback(async (config: PractitionerSessionConfig) => {
    // Reset state
    setState({
      isGenerating: true,
      progress: 0,
      error: null,
      result: null,
    });

    try {
      // Validate inputs
      if (!config.clientId && !config.groupSessionId) {
        throw new Error('Either clientId or groupSessionId must be provided');
      }

      if (!config.categoryId) {
        throw new Error('Category ID is required');
      }

      if (!config.questionnaireData) {
        throw new Error('Questionnaire data is required');
      }

      // Map questionnaire data to flat backend schema
      const flatConfig = mapQuestionnaireToBackendSchema(config.questionnaireData, {
        voiceGender: config.voiceGender,
        specific_voice_id: config.specific_voice_id,
        backgroundMusicFile: config.backgroundMusicFile,
      });

      // Build API request
      const request: PractitionerSessionCreateRequest = {
        // Metadata
        title: config.title,
        session_notes: config.sessionNotes,

        // Client/Group
        client_id: config.clientId,
        group_session_id: config.groupSessionId,

        // Category & length
        category_id: config.categoryId,
        session_length: flatConfig.session_length,

        // Session configuration (flat!)
        environment: flatConfig.environment,
        meditation_style: flatConfig.meditation_style,
        seeking_goal: flatConfig.seeking_goal,
        desired_feeling: flatConfig.desired_feeling,
        voice_style: flatConfig.voice_style,

        // Voice & audio
        voice_gender: flatConfig.voice_gender,
        specific_voice_id: flatConfig.specific_voice_id,
        background_music_file: flatConfig.background_music_file,
        include_background_sounds: flatConfig.include_background_sounds,
        background_music_volume_db: config.backgroundMusicVolumeDb || flatConfig.background_music_volume_db,

        // Personalization
        use_personal_biography: config.questionnaireData.usePersonalBiography ?? true,
        client_biography: flatConfig.client_biography,
        content_exclusions: config.contentExclusions || [],
        content_requirements: config.contentRequirements || [],
        session_directives: config.sessionDirectives,
        // FIX: Only send client names if explicitly provided (individual client sessions only)
        // For group sessions, these should be undefined
        client_first_name: config.clientFirstName !== undefined ? config.clientFirstName : flatConfig.client_first_name,
        client_last_name: config.clientLastName !== undefined ? config.clientLastName : flatConfig.client_last_name,
        // FIX: Don't send practitioner_name - backend derives it from database
        practitioner_name: config.practitionerName !== undefined ? config.practitionerName : flatConfig.practitioner_name,

        // Questionnaire (for cache key)
        questionnaire_responses: flatConfig.questionnaire_responses,
        custom_prompt: config.customPrompt,

        // Custom text mode fields
        custom_text_mode: config.custom_text_mode,
        custom_text: config.custom_text,
        custom_text_instructions: config.custom_text_instructions,

        // Audio mixing
        save_mixed_version: config.saveMixedVersion ?? true, // Default to true
      };

      // Create session and wait for completion
      const result = await apiClient.createAndWaitForSession(request, (progress) => {
        setState(prev => ({ ...prev, progress }));
      });

      // Success!
      setState({
        isGenerating: false,
        progress: 100,
        error: null,
        result: {
          sessionId: result.sessionId,
          audioUrl: result.audioUrl,
          mixedAudioUrl: result.mixedAudioUrl,
          backgroundAudioUrl: result.backgroundAudioUrl, // Single source of truth from backend
          generatedContent: result.generatedContent,
        },
      });

      return result;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';

      setState({
        isGenerating: false,
        progress: 0,
        error: errorMessage,
        result: null,
      });

      throw error;
    }
  }, [apiClient]);

  /**
   * Reset the hook state
   */
  const reset = useCallback(() => {
    setState({
      isGenerating: false,
      progress: 0,
      error: null,
      result: null,
    });
  }, []);

  return {
    // State
    isGenerating: state.isGenerating,
    progress: state.progress,
    error: state.error,
    result: state.result,

    // Actions
    generateSession,
    reset,

    // Computed
    hasResult: Boolean(state.result),
    hasError: Boolean(state.error),
  };
}

// ============================================================================
// CONVENIENCE EXPORT
// ============================================================================

export default usePractitionerSessionGeneration;
